import { useTranslation } from 'react-i18next'
import { type SafetyFunction } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  type RiskReductionMeasureFormData,
} from './RiskReductionMeasureForm.schema'
import { EhsrSelect } from '../hazards/EhsrSelect'
import { SafetyFunctionSelect } from './SafetyFunctionSelect'

type RiskReductionMeasureFormProps = {
  data: RiskReductionMeasureFormData
  onChange: (nextData: RiskReductionMeasureFormData) => void
  safetyFunctions?: SafetyFunction[]
}

type MeasureTypeOption = {
  value: RiskReductionMeasureFormData['type']
  label: string
}

export function RiskReductionMeasureForm({
  data,
  onChange,
  safetyFunctions = [],
}: RiskReductionMeasureFormProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const ehsrOptions = useMachineryDirectiveEhsrList()

  const measureTypeOptions: MeasureTypeOption[] = [
    { value: 'ISD', label: t('riskReductionMeasuresLibraryPage.form.measureTypes.ISD') },
    { value: 'SCP', label: t('riskReductionMeasuresLibraryPage.form.measureTypes.SCP') },
    { value: 'PPE', label: t('riskReductionMeasuresLibraryPage.form.measureTypes.PPE') },
    { value: 'OI', label: t('riskReductionMeasuresLibraryPage.form.measureTypes.OI') },
  ]

  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <Label htmlFor='rrm-name'>
          {t('riskReductionMeasuresLibraryPage.form.name')}{' '}
          <span className='text-destructive'>*</span>
        </Label>
        <Input
          id='rrm-name'
          value={data.name}
          onChange={(event) => {
            onChange({ ...data, name: event.target.value })
          }}
          placeholder={t('riskReductionMeasuresLibraryPage.form.namePlaceholder')}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='rrm-type'>
          {t('riskReductionMeasuresLibraryPage.form.type')}{' '}
          <span className='text-destructive'>*</span>
        </Label>
        <Select
          value={data.type}
          onValueChange={(value) => {
            if (!value) {
              return
            }
            const nextType = value as RiskReductionMeasureFormData['type']
            onChange({
              ...data,
              type: nextType,
              dependsOnControlSystem:
                nextType === 'SCP' ? data.dependsOnControlSystem : false,
              safetyFunctionId: nextType === 'SCP' ? data.safetyFunctionId : null,
            })
          }}
          items={measureTypeOptions}
        >
          <SelectTrigger id='rrm-type' className='w-full'>
            <SelectValue
              placeholder={t('riskReductionMeasuresLibraryPage.form.typePlaceholder')}
            />
          </SelectTrigger>
          <SelectContent>
            {measureTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {data.type === 'SCP' && (
        <div className='space-y-2'>
          <Label>
            {t('riskReductionMeasuresLibraryPage.form.dependsOnControlSystem')}
          </Label>
          <RadioGroup
            value={data.dependsOnControlSystem ? 'yes' : 'no'}
            onValueChange={(value) => {
              const dependsOnControlSystem = value === 'yes'
              onChange({
                ...data,
                dependsOnControlSystem,
                safetyFunctionId: dependsOnControlSystem
                  ? data.safetyFunctionId
                  : null,
              })
            }}
            className='flex gap-4'
          >
            <div className='flex items-center space-x-2'>
              <RadioGroupItem value='yes' id='rrm-depends-yes' />
              <Label htmlFor='rrm-depends-yes' className='font-normal cursor-pointer'>
                {t('riskReductionMeasuresLibraryPage.form.yes')}
              </Label>
            </div>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem value='no' id='rrm-depends-no' />
              <Label htmlFor='rrm-depends-no' className='font-normal cursor-pointer'>
                {t('riskReductionMeasuresLibraryPage.form.no')}
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}

      {data.type === 'SCP' && data.dependsOnControlSystem && (
        <div className='space-y-2'>
          <Label htmlFor='rrm-safety-function'>
            {t('riskReductionMeasuresLibraryPage.form.safetyFunction')}
          </Label>
          <SafetyFunctionSelect
            id='rrm-safety-function'
            value={data.safetyFunctionId}
            onChange={(safetyFunctionId) => {
              onChange({ ...data, safetyFunctionId })
            }}
            safetyFunctions={safetyFunctions}
          />
        </div>
      )}

      <div className='space-y-2'>
        <Label htmlFor='rrm-description'>
          {t('riskReductionMeasuresLibraryPage.form.description')}{' '}
          <span className='text-destructive'>*</span>
        </Label>
        <Textarea
          id='rrm-description'
          value={data.description}
          onChange={(event) => {
            onChange({ ...data, description: event.target.value })
          }}
          placeholder={t('riskReductionMeasuresLibraryPage.form.descriptionPlaceholder')}
          rows={3}
        />
      </div>

      <div className='space-y-2'>
        <Label>{t('hazardsPage.hazardDetail.ehsr')}</Label>
        <EhsrSelect
          options={ehsrOptions}
          value={data.ehsrClauseIds}
          onChange={(ehsrClauseIds) => {
            onChange({ ...data, ehsrClauseIds })
          }}
          placeholder={t('hazardsPage.hazardDetail.ehsrPlaceholder')}
        />
      </div>
    </div>
  )
}
